import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Table, Badge, Modal } from '../components/Common';

export default function ReportTemplates() {
  const { apiFetch, hasGroup, user, isMsp } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState(false);
  const [clients, setClients] = useState([]);
  const [accessModal, setAccessModal] = useState(null); // template being edited
  const [accessSelection, setAccessSelection] = useState([]); // selected client IDs
  const [savingAccess, setSavingAccess] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);

  const canManage = hasGroup('Report Admins');
  const isClientUser = !user?.isMsp && !user?.groups?.includes('Super Admins');

  const load = useCallback(async () => {
    const res = await apiFetch('/report-templates');
    setTemplates(await res.json());
  }, [apiFetch]);

  const loadClients = useCallback(async () => {
    if (!isMsp) return;
    try {
      const res = await apiFetch('/clients');
      if (res.ok) setClients(await res.json());
    } catch {}
  }, [apiFetch, isMsp]);

  useEffect(() => { load(); loadClients(); }, [load, loadClients]);

  const createNew = async () => {
    setCreating(true);
    try {
      const res = await apiFetch('/report-templates', {
        method: 'POST',
        body: JSON.stringify({ name: 'New Template', description: '' })
      });
      const data = await res.json();
      if (data.id) navigate(`/report-templates/${data.id}/edit`);
    } catch (err) {
      console.error('Create template error:', err);
    } finally {
      setCreating(false);
    }
  };

  const toggleDefault = async (id, isDefault) => {
    await apiFetch(`/report-templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ isDefault: !isDefault })
    });
    load();
  };

  const cloneTemplate = async (id) => {
    try {
      const res = await apiFetch(`/report-templates/${id}/clone`, { method: 'POST' });
      const data = await res.json();
      if (data.id) navigate(`/report-templates/${data.id}/edit`);
    } catch (err) {
      console.error('Clone template error:', err);
    }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await apiFetch(`/report-templates/${id}`, { method: 'DELETE' });
    load();
  };

  const exportTemplate = async (id, name) => {
    try {
      const res = await apiFetch(`/report-templates/${id}/export`);
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        alert(errData.error || 'Export failed');
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (name || 'template').replace(/[^a-zA-Z0-9_-]/g, '_');
      a.download = `${safeName}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export template error:', err);
      alert('Failed to export template');
    }
  };

  const importTemplate = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportError(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const token = sessionStorage.getItem('token');
      const res = await fetch('/api/report-templates/import', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        setImportModal(false);
        setImportFile(null);
        navigate(`/report-templates/${data.id}/edit`);
      } else {
        setImportError(data.error || 'Import failed');
      }
    } catch (err) {
      console.error('Import template error:', err);
      setImportError('Failed to import template');
    } finally {
      setImporting(false);
    }
  };

  const openAccessModal = (tmpl) => {
    setAccessModal(tmpl);
    setAccessSelection(tmpl.client_access || []);
  };

  const toggleClientAccess = (clientId) => {
    setAccessSelection(prev =>
      prev.includes(clientId) ? prev.filter(id => id !== clientId) : [...prev, clientId]
    );
  };

  const saveAccess = async () => {
    if (!accessModal) return;
    setSavingAccess(true);
    try {
      const res = await apiFetch(`/report-templates/${accessModal.id}/access`, {
        method: 'PUT',
        body: JSON.stringify({ clientAccess: accessSelection })
      });
      if (res.ok) {
        setAccessModal(null);
        load();
      } else {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'Failed to update access');
      }
    } catch (err) {
      alert('Failed to update access');
    } finally {
      setSavingAccess(false);
    }
  };

  /** Client users can only edit/delete their own client's templates, not shared MSP ones */
  const canModify = (row) => {
    if (!canManage) return false;
    if (!isClientUser) return true; // MSP users can modify anything
    // A template you created stays yours — covers report admins with no client_id,
    // whose templates land on the shared shelf (client_id null).
    if (row.created_by && row.created_by === user?.id && !row.client_id) return true;
    return !!row.client_id; // Client users can only modify templates with a client_id (their own)
  };

  /** Only MSP Report Admins can manage access on MSP templates */
  const canManageAccess = (row) => {
    if (!canManage || isClientUser) return false;
    return !row.client_id; // Only MSP templates (no client_id) have managed access
  };

  const getAccessCount = (row) => {
    const access = row.client_access || [];
    return access.length;
  };

  const columns = [
    { key: 'name', label: 'Name', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 600 }}>{v}</span>
        {row.client_id && (
          <span style={{
            fontSize: 10, fontWeight: 600, padding: '1px 6px',
            background: '#22c55e', color: '#fff', borderRadius: 8
          }}>CLIENT</span>
        )}
      </div>
    )},
    { key: 'description', label: 'Description', render: v => v ? (v.length > 60 ? v.substring(0, 60) + '...' : v) : '—' },
    ...(isMsp ? [{ key: 'client_access', label: 'Client Access', render: (v, row) => {
      if (row.client_id) return <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Owner only</span>;
      const count = getAccessCount(row);
      return (
        <span style={{ fontSize: 12, color: count > 0 ? '#22c55e' : 'var(--text-secondary)', fontWeight: count > 0 ? 600 : 400 }}>
          {count === 0 ? 'No clients' : `${count} client${count > 1 ? 's' : ''}`}
        </span>
      );
    }}] : []),
    { key: 'is_default', label: 'Default', render: v => v ? '⭐ Yes' : '' },
    { key: 'id', label: 'Actions', render: (v, row) => (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {canModify(row) && (
          <Button variant="primary" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); navigate(`/report-templates/${row.id}/edit`); }}>
            Edit
          </Button>
        )}
        {canManage && (
          <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); cloneTemplate(row.id); }}>
            Clone
          </Button>
        )}
        <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
          onClick={(e) => { e.stopPropagation(); exportTemplate(row.id, row.name); }}>
          Export
        </Button>
        {canManageAccess(row) && (
          <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); openAccessModal(row); }}>
            Client Access
          </Button>
        )}
        {canModify(row) && (
          <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); toggleDefault(row.id, row.is_default); }}>
            {row.is_default ? 'Unset Default' : 'Set Default'}
          </Button>
        )}
        {canModify(row) && !row.is_default && (
          <Button variant="danger" style={{ padding: '4px 12px', fontSize: 12 }}
            onClick={(e) => { e.stopPropagation(); deleteTemplate(row.id); }}>
            Delete
          </Button>
        )}
      </div>
    )}
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Report Templates</h1>
        {canManage && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={() => setImportModal(true)}>
              Import Template
            </Button>
            <Button onClick={createNew} disabled={creating}>
              {creating ? 'Creating...' : '+ New Template'}
            </Button>
          </div>
        )}
      </div>
      {!canManage && (
        <div style={{
          padding: '12px 16px', marginBottom: 16, borderRadius: 8,
          background: 'var(--bg-input)', border: '1px solid var(--border)',
          fontSize: 13, color: 'var(--text-secondary)'
        }}>
          You have read-only access to report templates. Contact an administrator to get the <strong>Report Admins</strong> group for create/edit permissions.
        </div>
      )}
      <Table columns={columns} data={templates} />

      {/* Client Access Modal */}
      <Modal isOpen={!!accessModal} onClose={() => !savingAccess && setAccessModal(null)}
        title={`Client Access — ${accessModal?.name || ''}`} width={500}>
        <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
          Select which clients can view and use this template for report generation. Clients will not be able to edit it.
        </div>
        {clients.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
            No clients found.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 11 }}
                onClick={() => setAccessSelection(clients.filter(c => c.is_active).map(c => c.id))}>
                Select All
              </Button>
              <Button variant="secondary" style={{ padding: '4px 12px', fontSize: 11 }}
                onClick={() => setAccessSelection([])}>
                Clear All
              </Button>
            </div>
            <div style={{
              maxHeight: 320, overflowY: 'auto', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', background: 'var(--bg-input)'
            }}>
              {clients.filter(c => c.is_active).map(c => (
                <label key={c.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                  borderBottom: '1px solid var(--border)', cursor: 'pointer',
                  background: accessSelection.includes(c.id) ? 'rgba(59, 130, 246, 0.08)' : 'transparent',
                  transition: 'background 0.15s'
                }}>
                  <input type="checkbox"
                    checked={accessSelection.includes(c.id)}
                    onChange={() => toggleClientAccess(c.id)}
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{c.name}</div>
                    {c.contact_name && (
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{c.contact_name}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </>
        )}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <Button variant="secondary" onClick={() => setAccessModal(null)} disabled={savingAccess}>Cancel</Button>
          <Button onClick={saveAccess} disabled={savingAccess}>
            {savingAccess ? 'Saving...' : 'Save Access'}
          </Button>
        </div>
      </Modal>

      {/* Import Template Modal */}
      <Modal isOpen={importModal} onClose={() => !importing && setImportModal(false)}
        title="Import Template" width={480}>
        <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--text-secondary)' }}>
          Select a previously exported template JSON file to import as a new template.
        </div>
        <input
          type="file"
          accept=".json"
          onChange={(e) => { setImportFile(e.target.files[0] || null); setImportError(null); }}
          style={{
            width: '100%', padding: '10px 14px', borderRadius: 'var(--radius)',
            border: '1px solid var(--border)', background: 'var(--bg-input)',
            color: 'var(--text)', fontSize: 14, marginBottom: 16, boxSizing: 'border-box'
          }}
        />
        {importError && (
          <div style={{
            padding: '8px 12px', marginBottom: 12, borderRadius: 'var(--radius)',
            background: '#7f1d1d', color: '#fca5a5', fontSize: 13
          }}>
            {importError}
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button variant="secondary"
            onClick={() => { setImportModal(false); setImportFile(null); setImportError(null); }}
            disabled={importing}>
            Cancel
          </Button>
          <Button onClick={importTemplate} disabled={importing || !importFile}>
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
